import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { HorizontalScrollRow } from '../common/horizontal-scroll';
import { Icon } from '../common/icon';

export interface MarketToolbarTab {
  id: string;
  label: string;
}

export interface MarketToolbarProps {
  tabs: MarketToolbarTab[];
  selectedTabId: string;
  onSelectTab: (id: string) => void;
  searchOpen?: boolean;
  searchQuery?: string;
  onToggleSearch?: () => void;
  onSearchChange?: (value: string) => void;
  className?: string;
}

/** Horizontal market-group pills plus the search toggle from the Figma Event Detail frame. */
export function MarketToolbar({
  tabs,
  selectedTabId,
  onSelectTab,
  searchOpen = false,
  searchQuery = '',
  onToggleSearch,
  onSearchChange,
  className,
}: MarketToolbarProps) {
  const { t } = useTranslation();
  const searchMarketsLabel = t('SearchMarket', { defaultValue: 'Search markets' });

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center gap-2">
        <HorizontalScrollRow
          aria-label={t('Market groups', { defaultValue: 'Market groups' })}
          className="min-w-0 flex-1 pl-0"
        >
          {tabs.map((tab) => {
            const selected = tab.id === selectedTabId;
            return (
              <button
                key={tab.id}
                type="button"
                role="listitem"
                onClick={() => onSelectTab(tab.id)}
                aria-pressed={selected}
                className={cn(
                  'shrink-0 rounded-full px-4 py-2 text-caption whitespace-nowrap',
                  selected
                    ? 'bg-action-secondary text-text-inverse'
                    : 'bg-bg-surface text-text-secondary',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </HorizontalScrollRow>
        <button
          type="button"
          onClick={onToggleSearch}
          aria-label={searchMarketsLabel}
          aria-pressed={searchOpen}
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-md border border-border-default',
            'bg-bg-surface text-icon-muted hover:bg-bg-elevated hover:text-icon-default',
            searchOpen && 'text-icon-default',
          )}
        >
          <Icon name="search" size={14} />
        </button>
      </div>
      {searchOpen ? (
        <input
          type="search"
          value={searchQuery}
          onChange={(event) => onSearchChange?.(event.target.value)}
          placeholder={searchMarketsLabel}
          autoFocus
          className={cn(
            'w-full rounded-md border border-border-default bg-bg-elevated px-3 py-2',
            // text-body-l (16px) avoids iOS Safari/Chrome focus zoom on inputs < 16px.
            'text-body-l text-text-primary outline-none placeholder:text-text-secondary',
            'focus-visible:border-border-focus',
          )}
        />
      ) : null}
    </div>
  );
}
